/**
 * OrbitPrint — the global queue: everything in line, held for review, failed, and recent
 * history, across all locations. Managers approve / decline / retry / cancel anything;
 * members see and manage only their own jobs (the server enforces both).
 * Live over the socket (slice 5): a "jobs changed" hint re-fetches the list;
 * the 5 s poll is the fallback and relaxes to 60 s while the line is open.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { orbitApi, errText, useOrbitAuth, useOrbitPath } from '../OrbitAuthContext';
import { useLiveRefresh, usePollMs } from '../live';
import { usePlan } from '../plan';
import { OrbitButton } from './OrbitShell';
import { JobLamp } from './PrintFileDialog';

const POLL_LIVE_MS = 60000;
const POLL_FALLBACK_MS = 5000;
const TABS = [
  { id: 'line', label: 'In line', statuses: ['pending', 'claimed'] },
  { id: 'held', label: 'Needs review', statuses: ['held'] },
  { id: 'failed', label: 'Failed', statuses: ['failed'] },
  { id: 'history', label: 'History', statuses: ['printed', 'declined', 'cancelled', 'expired'] },
];

function ago(iso) { try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ''; } }

export default function JobsPanel({ refreshKey = 0 }) {
  const { org } = useOrbitAuth();
  const manager = org?.role === 'owner' || org?.role === 'admin';
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState('line');
  const timer = useRef(null);
  const pollMs = usePollMs(POLL_LIVE_MS, POLL_FALLBACK_MS);

  const load = useCallback(async () => {
    try { const r = await orbitApi.get('/jobs?limit=200'); setJobs(r.data || []); }
    catch { setJobs((prev) => prev || []); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    timer.current = setInterval(load, pollMs);
    return () => clearInterval(timer.current);
  }, [load, pollMs]);

  useLiveRefresh('jobs', load);

  const counts = useMemo(() => {
    const c = {};
    for (const t of TABS) c[t.id] = (jobs || []).filter((j) => t.statuses.includes(j.status)).length;
    return c;
  }, [jobs]);
  const current = TABS.find((t) => t.id === tab);
  const rows = (jobs || []).filter((j) => current.statuses.includes(j.status));

  const act = async (fn, okMsg) => {
    try { const r = await fn(); toast.success(r?.data?.message || okMsg); load(); }
    catch (err) { toast.error(errText(err)); }
  };
  const approve = (j) => act(() => orbitApi.post(`/jobs/${j.id}/approve`), 'Approved — back in line');
  const decline = (j) => {
    const reason = window.prompt(`Decline "${j.title}"? Add a short reason for the sender (optional):`, '');
    if (reason === null) return;
    act(() => orbitApi.post(`/jobs/${j.id}/decline`, { reason }), 'Declined');
  };
  const retry = (j) => act(() => orbitApi.post(`/jobs/${j.id}/retry`), 'Back in line, at the front');
  const cancel = (j) => { if (window.confirm(`Cancel "${j.title}"?`)) act(() => orbitApi.post(`/jobs/${j.id}/cancel`), 'Cancelled'); };

  if (jobs !== null && jobs.length === 0) return null;   // nothing sent yet — the dashboard stays clean

  return (
    <section className="orbit-card orbit-boot mt-8 p-6" style={{ animationDelay: '250ms' }} data-testid="orbit-jobs-panel">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="orbit-label m-0">Queue</p>
          <h2 className="orbit-display mt-1" style={{ fontSize: 28 }}>Documents</h2>
        </div>
        <div className="flex gap-1 p-1 rounded-full" style={{ border: '1px solid var(--ob-rule)', background: 'var(--ob-canvas-2)' }} role="tablist">
          {TABS.map((t) => (
            <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className="text-xs font-semibold rounded-full px-3 py-1.5"
              style={{ background: tab === t.id ? 'var(--ob-surface-3)' : 'transparent', color: tab === t.id ? 'var(--ob-ink)' : 'var(--ob-mute)', border: 0, cursor: 'pointer', font: 'inherit' }}
              data-testid={`orbit-jobs-tab-${t.id}`}>
              {t.label}{counts[t.id] ? <span className="orbit-mono ml-1.5" style={{ color: t.id === 'held' && counts[t.id] ? 'var(--ob-amber)' : 'inherit' }}>{counts[t.id]}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {jobs === null ? (
        <p className="orbit-mono text-sm orbit-muted mt-4">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm orbit-ink2 mt-4 m-0">{{ line: 'Nothing in line right now.', held: 'Nothing waiting for review.', failed: 'No failed jobs.', history: 'No finished jobs yet.' }[tab]}</p>
      ) : (
        <ul className="list-none p-0 m-0 mt-2">
          {rows.map((j) => (
            <li key={j.id} className="orbit-prow" data-testid={`orbit-job-${j.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-[15px] truncate">{j.title}</span>
                  {j.priority === 'front' && j.status === 'pending' && <span className="orbit-label" style={{ color: 'var(--ob-green)' }}>retry · front</span>}
                </div>
                <p className="orbit-mono text-[11px] orbit-muted mt-0.5 m-0 truncate">
                  {j.printer_name} · {j.location_name} · {j.created_by_name} · {ago(j.created_at)}{j.copies > 1 ? ` · ${j.copies} copies` : ''}
                </p>
                <p className="text-sm orbit-ink2 mt-1 m-0" data-testid={`orbit-job-message-${j.id}`}>{j.message}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <JobLamp status={j.status} />
                <div className="flex gap-3 text-xs">
                  {j.status === 'held' && manager && (<>
                    <button className="orbit-link" onClick={() => approve(j)} data-testid={`orbit-job-approve-${j.id}`}>Approve</button>
                    <button className="orbit-link is-danger" onClick={() => decline(j)} data-testid={`orbit-job-decline-${j.id}`}>Decline</button>
                  </>)}
                  {['pending', 'held'].includes(j.status) && <button className="orbit-link is-muted" onClick={() => cancel(j)} data-testid={`orbit-job-cancel-${j.id}`}>Cancel</button>}
                  {j.status === 'claimed' && !j.cancel_requested && <button className="orbit-link is-muted" onClick={() => cancel(j)}>Ask HUB to stop</button>}
                  {['failed', 'declined', 'cancelled'].includes(j.status) && <button className="orbit-link" onClick={() => retry(j)} data-testid={`orbit-job-retry-${j.id}`}>Retry at the front</button>}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!manager && <p className="text-xs orbit-muted mt-3 m-0">You see your own documents. A manager sees everyone's and reviews held jobs.</p>}
    </section>
  );
}

/** "12 / 30 docs · Free" — links to the plans page; amber when the quota is used up. */
export function UsageBadge() {
  const { plan: u } = usePlan();
  const p = useOrbitPath();
  if (!u) return null;
  const over = u.limit != null && u.documents >= u.limit;
  const label = u.plan_label || u.plan;
  return (
    <Link to={p('/plans')} className="orbit-mono text-xs hidden md:inline no-underline" style={{ color: over ? 'var(--ob-amber)' : 'var(--ob-mute)' }}
      title={u.limit != null ? `Documents sent this month on the ${label} plan — resets ${new Date(u.resets_on).toLocaleDateString('en-PH', { month: 'long', day: 'numeric' })}` : 'Documents sent this month'}
      data-testid="orbit-usage">
      {u.documents}{u.limit != null ? ` / ${u.limit}` : ''} docs · {label}
    </Link>
  );
}

export { OrbitButton };
